import { type FC, useEffect } from "react";
import { Button } from "flowbite-react";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiX,
} from "react-icons/hi";

interface ContractSuccessModalProps {
  show: boolean;
  onClose: () => void;
  success: boolean;
  message: string;
  actionLabel?: string;
  autoCloseDelay?: number; // In milliseconds, default 3000
}

const ContractSuccessModal: FC<ContractSuccessModalProps> = ({
  show,
  onClose,
  success,
  message,
  actionLabel,
  autoCloseDelay = 3000,
}) => {
  // Auto-close on success after delay
  useEffect(() => {
    if (show && success && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, success, autoCloseDelay, onClose]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900 bg-opacity-50">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
          type="button"
          aria-label="Schließen"
        >
          <HiX className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
            {success ? (
              <HiCheckCircle className="h-16 w-16 text-green-500" />
            ) : (
              <HiExclamationCircle className="h-16 w-16 text-yellow-500" />
            )}
          </div>

          {/* Title */}
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
            {success ? "Erfolgreich!" : "Hinweis"}
          </h3>

          {/* Message */}
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            {message}
          </p>

          {/* Button */}
          <Button
            color={success ? "success" : "warning"}
            onClick={onClose}
            className="mx-auto"
          >
            {actionLabel ?? (success ? "OK" : "Verstanden")}
          </Button>

          {/* Auto-close indicator for success */}
          {success && autoCloseDelay > 0 && (
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Schließt automatisch in {autoCloseDelay / 1000} Sekunden...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractSuccessModal;
